import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Zap, 
  Sun, 
  CarFront, 
  Droplets, 
  MoveVertical,
  Menu,
  LogOut,
  ChevronLeft,
  ChevronDown,
  Battery,
  Users,
  Map,
  Settings,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import Footer from './Footer';
import NotificationsDropdown from './NotificationsDropdown';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/energia', icon: Zap, label: 'Energia' },
  { path: '/fotovoltaico', icon: Sun, label: 'Fotovoltaico' },
  { 
    path: '/carregamento', 
    icon: CarFront, 
    label: 'Postos Carregamento VE',
    submenus: [
      { path: '/carregamento', label: 'Visão Geral', icon: LayoutDashboard },
      { path: '/carregamento/mapa', label: 'Mapa de Postos', icon: Map },
      { path: '/carregamento/postos', label: 'Gerir Postos', icon: Battery },
      { path: '/carregamento/utilizadores', label: 'Gerir Utilizadores', icon: Users }
    ]
  },
  { path: '/agua', icon: Droplets, label: 'Água' },
  { path: '/elevadores', icon: MoveVertical, label: 'Elevadores' },
  { 
    path: '/configuracoes', 
    icon: Settings, 
    label: 'Configurações',
    submenus: [
      { path: '/configuracoes/alertas', label: 'Alertas', icon: Bell },
      { path: '/configuracoes/agua', label: 'Água (API)', icon: Droplets },
      { path: '/configuracoes/energia', label: 'Energia (XLSX)', icon: Zap },
      { path: '/configuracoes/fotovoltaico', label: 'Fotovoltaico (Shelly)', icon: Sun },
      { path: '/configuracoes/elevadores', label: 'Elevadores (PLC)', icon: MoveVertical }
    ]
  },
];

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSubmenu = (path) => {
    setExpandedMenu(expandedMenu === path ? null : path);
  };

  const isActive = (path) => {
    if (path === '/carregamento') {
      return location.pathname.startsWith('/carregamento');
    }
    if (path === '/configuracoes') {
      return location.pathname.startsWith('/configuracoes');
    }
    return location.pathname === path;
  };

  const isSubmenuActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30 h-16">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <img 
              src="/logo-ip.png" 
              alt="Infraestruturas de Portugal" 
              className="h-10 object-contain"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <NotificationsDropdown />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.foto} alt={user?.nome} />
                  <AvatarFallback className="bg-[#003D7A] text-white">
                    {user?.nome?.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline-block text-sm font-medium">{user?.nome}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.nome}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-gray-500">{user?.cargo}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/perfil')}>
                <ChevronLeft className="mr-2 h-4 w-4 rotate-180" />
                <span>Ver Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Terminar Sessão</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 transition-all duration-300 z-20 overflow-y-auto ${
          sidebarOpen ? 'w-64' : 'w-0 lg:w-16'
        }`}
      >
        <nav className="p-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const hasSubmenus = item.submenus && item.submenus.length > 0;
            const isMenuActive = isActive(item.path);
            const isExpanded = expandedMenu === item.path;
            
            return (
              <div key={item.path}>
                <button
                  onClick={() => {
                    if (hasSubmenus) {
                      toggleSubmenu(item.path);
                    } else {
                      navigate(item.path);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${
                    isMenuActive
                      ? 'bg-[#003D7A] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && (
                      <span className="text-sm font-medium whitespace-nowrap overflow-hidden">
                        {item.label}
                      </span>
                    )}
                  </div>
                  {sidebarOpen && hasSubmenus && (
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </button>
                
                {/* Submenus */}
                {hasSubmenus && sidebarOpen && isExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.submenus.map((submenu) => {
                      const SubIcon = submenu.icon;
                      const isSubActive = isSubmenuActive(submenu.path);
                      
                      return (
                        <button
                          key={submenu.path}
                          onClick={() => navigate(submenu.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                            isSubActive
                              ? 'bg-blue-50 text-[#003D7A] font-medium'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <SubIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="whitespace-nowrap overflow-hidden">
                            {submenu.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        
        {/* Toggle button for desktop */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden lg:flex absolute -right-3 top-4 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center hover:bg-gray-50 transition-all"
        >
          <ChevronLeft
            className={`h-4 w-4 transition-transform ${
              sidebarOpen ? '' : 'rotate-180'
            }`}
          />
        </button>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-16 transition-all duration-300 min-h-screen flex flex-col ${
          sidebarOpen ? 'lg:pl-64' : 'lg:pl-16'
        }`}
      >
        <div className="p-6 flex-grow">
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default Layout;
